import { MONTH_LABELS } from "constants/dateTime";
import {
  DATE_VARIANTS,
  formatDate as commonFormatDate,
} from "utils/formatDate";

export const DAY_MS = 86400000;

export const TICK_MODE_MONTH = "month";
export const TICK_MODE_DAY = "day";

export const SCALES = [
  { label: "1 Year", days: 365, mode: TICK_MODE_MONTH, step: 3 },
  { label: "9 Months", days: 273, mode: TICK_MODE_MONTH, step: 1 },
  { label: "6 Months", days: 182, mode: TICK_MODE_MONTH, step: 1 },
  { label: "3 Months", days: 91, mode: TICK_MODE_DAY, step: 7 },
  { label: "1 Month", days: 30, mode: TICK_MODE_DAY, step: 2 },
  { label: "2 Weeks", days: 14, mode: TICK_MODE_DAY, step: 1 },
];

export const DEFAULT_SCALE_INDEX = 4;

/**
 * The grid is one scroller with sticky label and due columns, so these widths are shared by
 * the layout (as custom properties) and by the scroll maths. They live here so the two can
 * never disagree.
 */
export const ROW_HEIGHT_PX = 46;
export const HEAD_HEIGHT_PX = 44;
export const LABEL_COLUMN_PX = 260;
export const TEAM_COLUMN_PX = 96;
export const DUE_COLUMN_PX = 128;

// Below this many rows every row is rendered: windowing buys nothing and costs measurement.
export const VIRTUALIZE_THRESHOLD = 60;
const ROW_OVERSCAN = 6;

export const visibleRowRange = ({ rowCount, scrollTop, viewportHeight }) => {
  if (rowCount <= VIRTUALIZE_THRESHOLD) {
    return { start: 0, end: rowCount };
  }
  const start = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT_PX) - ROW_OVERSCAN
  );
  const end = Math.min(
    rowCount,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT_PX) + ROW_OVERSCAN
  );
  return { start, end: Math.max(end, start) };
};

// Today sits this far in from the left, leaving the majority of the view showing past activity.
export const TODAY_VIEWPORT_RATIO = 0.8;

const MIN_DOMAIN_DAYS = 60;
const DOMAIN_PADDING_RATIO = 1.06;
const MIN_SEGMENT_PERCENT = 0.3;

const LABEL_CHAR_PX = 6.4;
const LABEL_PADDING_PX = 16;
const ELLIPSIS_MIN_PX = 29;
const MINI_MIN_PX = 17;

export const LABEL_MODE_FULL = "full";
export const LABEL_MODE_TRUNC = "trunc";
export const LABEL_MODE_MINI = "mini";
export const LABEL_MODE_NONE = "none";

export const startOfUtcDay = (ms) => {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const toMs = (value) => {
  if (value == null) {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

export const formatDate = (ms, withYear = false) => {
  if (ms == null) {
    return "";
  }

  const format = withYear ? DATE_VARIANTS.SHORT : DATE_VARIANTS.SHORT_DAY_MONTH;
  return commonFormatDate(ms, format);
};

export const LESS_THAN_A_DAY = "less than a day";

export const formatDayCount = (days) =>
  days < 1 ? LESS_THAN_A_DAY : `${days} ${days === 1 ? "day" : "days"}`;

/**
 * Every date on the chart is rendered in UTC, but "due today" has to mean today on the viewer's
 * own calendar: bucketing the current instant in UTC rolls it forward a day for anyone west of
 * UTC once their evening passes UTC midnight, and the Due column would then call tomorrow today.
 */
export const startOfViewerDay = (ms) => {
  const date = new Date(ms);
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

export const describeDeadline = (deadlineMs, todayMs, closed) => {
  if (deadlineMs == null) {
    return { label: "—", relative: "—", overdue: false };
  }
  const diff = Math.round(
    (startOfUtcDay(deadlineMs) - startOfViewerDay(todayMs)) / DAY_MS
  );
  if (closed) {
    return {
      label: formatDate(deadlineMs),
      relative: "closed",
      overdue: false,
    };
  }
  if (diff === 0) {
    return {
      label: formatDate(deadlineMs),
      relative: "due today",
      overdue: false,
    };
  }
  if (diff > 0) {
    return {
      label: formatDate(deadlineMs),
      relative: `in ${formatDayCount(diff)}`,
      overdue: false,
    };
  }
  return {
    label: formatDate(deadlineMs),
    relative: `${formatDayCount(Math.abs(diff))} overdue`,
    overdue: true,
  };
};

/**
 * The scrollable domain: the data's own range, widened so today can sit at
 * TODAY_VIEWPORT_RATIO, then padded by a full visible span each side so there is always
 * empty time to scroll into rather than a hard stop at the first and last bar.
 */
export const buildDomain = (rows, todayMs) => {
  const stamps = [];
  rows.forEach((row) => {
    (row.periods ?? []).forEach((period) => {
      const from = toMs(period.from);
      const to = toMs(period.to);
      if (from != null) {
        stamps.push(from);
      }
      if (to != null) {
        stamps.push(to);
      }
    });
    const deadline = toMs(row.deadline);
    if (deadline != null) {
      stamps.push(deadline);
    }
  });

  // Reduced rather than spread: a few thousand rows put tens of thousands of stamps in here,
  // which is the order at which an argument list stops being safe.
  const minMs = stamps.length
    ? stamps.reduce((min, ms) => (ms < min ? ms : min))
    : todayMs - 30 * DAY_MS;
  const maxMs = stamps.reduce((max, ms) => (ms > max ? ms : max), todayMs);
  const dataSpan =
    Math.max(maxMs - minMs, MIN_DOMAIN_DAYS * DAY_MS) * DOMAIN_PADDING_RATIO;
  const visibleSpan = Math.max(
    dataSpan,
    (todayMs - minMs) / TODAY_VIEWPORT_RATIO,
    MIN_DOMAIN_DAYS * DAY_MS
  );

  const start = minMs - visibleSpan;
  const end = Math.max(maxMs, todayMs) + visibleSpan;
  return { start, end, width: end - start };
};

export const percentOf = (ms, domain) =>
  ((ms - domain.start) / domain.width) * 100;

export const clampedPercentOf = (ms, domain) =>
  percentOf(Math.min(Math.max(ms, domain.start), domain.end), domain);

export const isInDomain = (ms, domain) =>
  ms != null && ms >= domain.start && ms <= domain.end;

/** How many times wider than the viewport the full domain is, at the chosen scale. */
export const zoomFor = (domain, scale) =>
  Math.max(domain.width / (scale.days * DAY_MS), 1);

/**
 * Ticks are generated for the visible window plus slack rather than the whole padded
 * domain, so a one-year domain at a two-week scale does not emit thousands of labels.
 */
export const buildTicks = ({ domain, scale, zoom, view, todayMs, trackWidthPx }) => {
  const visibleSpan = domain.width / zoom;
  const right = view
    ? domain.start + domain.width * view.right
    : Math.min(todayMs + visibleSpan * (1 - TODAY_VIEWPORT_RATIO), domain.end);
  const left = view
    ? domain.start + domain.width * view.left
    : right - visibleSpan;
  const slack = visibleSpan * (scale.mode === TICK_MODE_DAY ? 2.5 : 6);
  const from = Math.max(domain.start, left - slack);
  const to = Math.min(domain.end, right + slack);

  const ticks =
    scale.mode === TICK_MODE_MONTH
      ? buildMonthTicks(from, to, domain, scale, todayMs)
      : buildDayTicks(from, to, domain, scale);

  return {
    ticks: filterTicks(ticks, trackWidthPx, zoom),
    minorTicks: buildMonthBoundaries(from, to, domain, scale),
    generatedRange: {
      left: (from - domain.start) / domain.width,
      right: (to - domain.start) / domain.width,
    },
  };
};

const MAX_TICKS = 300;
const MIN_TICK_GAP_PX = 48;

const filterTicks = (ticks, trackWidthPx, zoom) => {
  const trackTotalPx = trackWidthPx * zoom;
  const result = [];
  let lastLeftPx = -Infinity;
  for (const tick of ticks) {
    const leftPx = (tick.left / 100) * trackTotalPx;
    if (leftPx - lastLeftPx >= MIN_TICK_GAP_PX) {
      result.push(tick);
      lastLeftPx = leftPx;
    }
  }
  return result;
};

const buildMonthBoundaries = (from, to, domain, scale) => {
  const boundaries = [];
  const first = new Date(from);
  let year = first.getUTCFullYear();
  let month = first.getUTCMonth() + (first.getUTCDate() > 1 ? 1 : 0);
  while (month > 11) {
    month -= 12;
    year += 1;
  }
  for (let i = 0; i < MAX_TICKS; i += 1) {
    const ms = Date.UTC(year, month, 1);
    if (ms > to) {
      break;
    }
    const labelled = scale.mode === TICK_MODE_MONTH && month % scale.step === 0;
    if (ms >= from && !labelled) {
      boundaries.push({
        key: `minor-${ms}`,
        left: clampedPercentOf(ms, domain),
      });
    }
    month += 1;
    while (month > 11) {
      month -= 12;
      year += 1;
    }
  }
  return boundaries;
};

const buildMonthTicks = (from, to, domain, scale, todayMs) => {
  const ticks = [];
  const currentYear = new Date(todayMs).getUTCFullYear();
  const first = new Date(from);
  let year = first.getUTCFullYear();
  let month =
    Math.ceil(
      (first.getUTCMonth() + (first.getUTCDate() > 1 ? 1 : 0)) / scale.step
    ) * scale.step;
  while (month > 11) {
    month -= 12;
    year += 1;
  }
  for (let i = 0; i < MAX_TICKS; i += 1) {
    const ms = Date.UTC(year, month, 1);
    if (ms > to) {
      break;
    }
    if (ms >= from) {
      const showYear =
        year !== currentYear && (month === 0 || scale.step === 3);
      ticks.push({
        key: `tick-${ms}`,
        left: clampedPercentOf(ms, domain),
        label: `${MONTH_LABELS[month]}${showYear ? ` '${String(year).slice(2)}` : ""}`,
      });
    }
    month += scale.step;
    while (month > 11) {
      month -= 12;
      year += 1;
    }
  }
  return ticks;
};

const buildDayTicks = (from, to, domain, scale) => {
  const ticks = [];
  let ms = startOfUtcDay(from);
  if (scale.step === 7) {
    const dayOfWeek = new Date(ms).getUTCDay();
    ms += ((8 - dayOfWeek) % 7) * DAY_MS;
  }
  for (
    let i = 0;
    i < MAX_TICKS && ms <= to;
    i += 1, ms += scale.step * DAY_MS
  ) {
    if (ms < from) {
      continue;
    }
    ticks.push({
      key: `tick-${ms}`,
      left: clampedPercentOf(ms, domain),
      label: formatDate(ms),
    });
  }
  return ticks;
};

export const resolveLabelMode = (widthPx, label) => {
  const needed = (label?.length ?? 0) * LABEL_CHAR_PX + LABEL_PADDING_PX;
  if (widthPx >= needed) {
    return LABEL_MODE_FULL;
  }
  if (widthPx >= ELLIPSIS_MIN_PX) {
    return LABEL_MODE_TRUNC;
  }
  return widthPx >= MINI_MIN_PX ? LABEL_MODE_MINI : LABEL_MODE_NONE;
};

/**
 * Places one row's periods on the domain. No bar crosses today, no two bars overlap, and no
 * bar disappears entirely — a period too short to see still gets a sliver so its recorded
 * gate answers stay reachable.
 */
export const buildRowSegments = ({ row, domain, todayMs, trackWidthPx }) => {
  const periods = (row.periods ?? []).filter(
    (period) => toMs(period.from) != null
  );
  const todayPercent = percentOf(todayMs, domain);

  const bounds = periods.map((period) => {
    const from = toMs(period.from);
    const to = period.open ? todayMs : (toMs(period.to) ?? from);
    const left = Math.min(Math.max(percentOf(from, domain), 0), todayPercent);
    const right = Math.min(percentOf(to, domain), todayPercent);
    return { period, left, right: Math.max(right, left) };
  });

  // Laid out right to left so a stage that lasted minutes borrows the room it needs from
  // the stage before it instead of spilling over today or over its neighbour.
  const slivers = new Set();
  let limit = todayPercent;
  for (let index = bounds.length - 1; index >= 0; index -= 1) {
    const bound = bounds[index];
    bound.right = Math.min(bound.right, limit);
    bound.left = Math.min(bound.left, bound.right);
    if (bound.right - bound.left < MIN_SEGMENT_PERCENT) {
      bound.left = bound.right - MIN_SEGMENT_PERCENT;
      slivers.add(index);
    }
    limit = bound.left;
  }

  return bounds.map((bound, index) => {
    const isSliver = slivers.has(index);
    const width = isSliver ? MIN_SEGMENT_PERCENT : bound.right - bound.left;
    const widthPx = (width / 100) * trackWidthPx;
    // The bar is widened to stay visible, but its day count is not: the backend emits a
    // zero-length period per gate crossed in a multi-stage jump, and rounding those up to a
    // day each would overstate every jumped row's total time on the board.
    const days = Math.round(
      ((bound.period.open
        ? todayMs
        : (toMs(bound.period.to) ?? toMs(bound.period.from))) -
        toMs(bound.period.from)) /
        DAY_MS
    );

    return {
      period: bound.period,
      left: bound.left,
      width,
      isSliver,
      labelMode: resolveLabelMode(widthPx, bound.period.stageName),
      days,
    };
  });
};

export const scaleIndexToFit = (rows, todayMs) => {
  const domainStamps = [];
  rows.forEach((row) => {
    (row.periods ?? []).forEach((period) => {
      const from = toMs(period.from);
      if (from != null) {
        domainStamps.push(from);
      }
    });
  });
  if (!domainStamps.length) {
    return DEFAULT_SCALE_INDEX;
  }
  const spanDays = (todayMs - Math.min(...domainStamps)) / DAY_MS;
  for (let i = SCALES.length - 1; i >= 0; i -= 1) {
    if (SCALES[i].days >= spanDays) {
      return i;
    }
  }
  return 0;
};
